import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, Clock, Plane, Phone, Mail, Building2, User, Briefcase, MessageSquare } from "lucide-react";
import { MarketingNav, MarketingFooter } from "./MarketingLayout";
import "../../marketing.css";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  "CEO / COO",
  "VP / Head of Ground Operations",
  "Director of Operations",
  "Station Manager",
  "Operations Control Manager",
  "Ramp Supervisor",
  "Safety Manager",
  "IT / Digital Transformation",
  "Other",
];

const FLEET_SIZES = [
  "1–5 aircraft",
  "6–15 aircraft",
  "16–30 aircraft",
  "31–50 aircraft",
  "50+ aircraft",
];

export default function MarketingDemo() {
  const [form, setForm] = useState({
    name: "", airline: "", role: "", email: "", phone: "", fleet_size: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.airline || !form.email || !form.role) {
      setError("Please fill in all required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/demo-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSubmitted(true);
    } catch (err) {
      setError("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#07111f] text-white marketing-font-body">
      <MarketingNav />

      {/* ── HERO ── */}
      <section className="pt-28 pb-12 relative overflow-hidden grid-texture">
        <div className="absolute inset-0" style={{ background: "radial-gradient(ellipse 60% 50% at 50% 30%, rgba(249,115,22,0.07) 0%, transparent 70%)" }} />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 px-3 py-1.5 rounded mb-6">
            <span className="w-1.5 h-1.5 rounded-full bg-orange-500 live-dot" />
            <span className="text-[10px] text-orange-400 marketing-font-mono uppercase font-bold tracking-widest">Request a Demo</span>
          </div>
          <h1 className="text-4xl lg:text-5xl font-black marketing-font-heading leading-tight mb-4">
            See GroundShield at your stations
          </h1>
          <p className="text-lg text-slate-400 max-w-xl mx-auto">
            30-minute live walkthrough with real data from your station. No slides. No sales script.
          </p>
        </div>
      </section>

      {/* ── MAIN ── */}
      <section className="pb-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid lg:grid-cols-5 gap-10 items-start">

            {/* ── LEFT: What to expect ── */}
            <div className="lg:col-span-2 space-y-6 lg:sticky lg:top-24">
              <div className="bg-[#0c1929]/60 border border-orange-500/10 p-6 rounded-xl">
                <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-4">What to expect</div>
                {[
                  { icon: Clock, title: "30-minute live session", body: "Live walkthrough of GroundShield with demo data from a real SME airline — no slides, no fluff." },
                  { icon: Plane, title: "Your stations, your data", body: "We configure the demo for your fleet size and base structure so it looks like your operation." },
                  { icon: CheckCircle, title: "End-to-end workflow", body: "From Turnaround Control to Ramp App to AI Delay Prediction — you see every module in action." },
                ].map((item, i) => {
                  const Icon = item.icon;
                  return (
                    <div key={i} className="flex gap-3 mb-5">
                      <div className="w-8 h-8 bg-orange-500/10 border border-orange-500/20 rounded flex items-center justify-center shrink-0 mt-0.5">
                        <Icon size={14} className="text-orange-400" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-white marketing-font-heading mb-1">{item.title}</div>
                        <div className="text-xs text-slate-500 leading-relaxed">{item.body}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="bg-[#0c1929]/40 border border-white/5 p-6 rounded-xl">
                <div className="text-[10px] text-slate-500 marketing-font-mono uppercase tracking-widest font-bold mb-4">Response SLA</div>
                <div className="space-y-3">
                  {[
                    { label: "Initial response", value: "< 4 hours" },
                    { label: "Demo scheduling", value: "Within 24 hours" },
                    { label: "Post-demo follow-up", value: "Same day" },
                  ].map((r, i) => (
                    <div key={i} className="flex items-center justify-between">
                      <span className="text-xs text-slate-500">{r.label}</span>
                      <span className="text-xs font-bold text-orange-400 marketing-font-mono">{r.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-[11px] text-slate-700 leading-relaxed pl-1">
                Already using a legacy GHM system? We'll show you an exact side-by-side comparison.
              </div>
            </div>

            {/* ── RIGHT: Form ── */}
            <div className="lg:col-span-3">
              {submitted ? (
                <div className="bg-[#0c1929]/60 border border-green-500/30 rounded-xl p-10 text-center">
                  <div className="w-14 h-14 bg-green-500/10 border border-green-500/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle size={28} className="text-green-400" />
                  </div>
                  <h2 className="text-2xl font-black marketing-font-heading mb-3">Request received</h2>
                  <p className="text-slate-400 mb-8 max-w-md mx-auto text-sm leading-relaxed">
                    We'll review your request and reach out within 4 hours to schedule your demo. Check your inbox at <span className="text-orange-400 font-semibold">{form.email}</span>.
                  </p>
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Link to="/marketing"
                      className="flex items-center gap-2 bg-orange-500 hover:bg-orange-400 text-white font-bold px-6 py-3 transition-all">
                      Back to Home <ArrowRight size={15} />
                    </Link>
                    <Link to="/login"
                      className="flex items-center gap-2 border border-white/15 hover:border-white/30 text-slate-300 px-6 py-3 transition-all">
                      Sign In to Platform
                    </Link>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleSubmit}
                  className="bg-[#0c1929]/60 border border-orange-500/10 rounded-xl p-8"
                  data-testid="demo-request-form">

                  <div className="text-[10px] text-orange-400 marketing-font-mono uppercase tracking-widest font-bold mb-6">Your details</div>

                  <div className="grid sm:grid-cols-2 gap-5 mb-5">
                    {/* Name */}
                    <div>
                      <label className="block text-[10px] text-slate-500 marketing-font-mono uppercase tracking-widest mb-2">
                        Full Name <span className="text-orange-400">*</span>
                      </label>
                      <div className="relative">
                        <User size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                        <input
                          name="name" value={form.name} onChange={handleChange}
                          placeholder="Rajesh Mehta"
                          className="w-full bg-[#07111f] border border-white/10 focus:border-orange-500/50 text-white text-sm pl-9 pr-4 py-3 outline-none transition-colors marketing-font-body placeholder:text-slate-700"
                          data-testid="demo-name-input"
                        />
                      </div>
                    </div>

                    {/* Airline */}
                    <div>
                      <label className="block text-[10px] text-slate-500 marketing-font-mono uppercase tracking-widest mb-2">
                        Airline / Company <span className="text-orange-400">*</span>
                      </label>
                      <div className="relative">
                        <Building2 size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                        <input
                          name="airline" value={form.airline} onChange={handleChange}
                          placeholder="Akasa Air"
                          className="w-full bg-[#07111f] border border-white/10 focus:border-orange-500/50 text-white text-sm pl-9 pr-4 py-3 outline-none transition-colors marketing-font-body placeholder:text-slate-700"
                          data-testid="demo-airline-input"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5 mb-5">
                    {/* Role */}
                    <div>
                      <label className="block text-[10px] text-slate-500 marketing-font-mono uppercase tracking-widest mb-2">
                        Your Role <span className="text-orange-400">*</span>
                      </label>
                      <div className="relative">
                        <Briefcase size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                        <select
                          name="role" value={form.role} onChange={handleChange}
                          className="w-full bg-[#07111f] border border-white/10 focus:border-orange-500/50 text-white text-sm pl-9 pr-4 py-3 outline-none transition-colors marketing-font-body appearance-none"
                          data-testid="demo-role-select"
                        >
                          <option value="" className="text-slate-600">Select your role</option>
                          {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                        </select>
                      </div>
                    </div>

                    {/* Fleet Size */}
                    <div>
                      <label className="block text-[10px] text-slate-500 marketing-font-mono uppercase tracking-widest mb-2">
                        Fleet Size
                      </label>
                      <div className="relative">
                        <Plane size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                        <select
                          name="fleet_size" value={form.fleet_size} onChange={handleChange}
                          className="w-full bg-[#07111f] border border-white/10 focus:border-orange-500/50 text-white text-sm pl-9 pr-4 py-3 outline-none transition-colors marketing-font-body appearance-none"
                          data-testid="demo-fleet-select"
                        >
                          <option value="">Select fleet size</option>
                          {FLEET_SIZES.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5 mb-5">
                    {/* Email */}
                    <div>
                      <label className="block text-[10px] text-slate-500 marketing-font-mono uppercase tracking-widest mb-2">
                        Work Email <span className="text-orange-400">*</span>
                      </label>
                      <div className="relative">
                        <Mail size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                        <input
                          name="email" value={form.email} onChange={handleChange} type="email"
                          placeholder="rajesh@airline.com"
                          className="w-full bg-[#07111f] border border-white/10 focus:border-orange-500/50 text-white text-sm pl-9 pr-4 py-3 outline-none transition-colors marketing-font-body placeholder:text-slate-700"
                          data-testid="demo-email-input"
                        />
                      </div>
                    </div>

                    {/* Phone */}
                    <div>
                      <label className="block text-[10px] text-slate-500 marketing-font-mono uppercase tracking-widest mb-2">
                        Phone (optional)
                      </label>
                      <div className="relative">
                        <Phone size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-600" />
                        <input
                          name="phone" value={form.phone} onChange={handleChange}
                          placeholder="+91 98XXXXXXXX"
                          className="w-full bg-[#07111f] border border-white/10 focus:border-orange-500/50 text-white text-sm pl-9 pr-4 py-3 outline-none transition-colors marketing-font-body placeholder:text-slate-700"
                          data-testid="demo-phone-input"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="mb-6">
                    <label className="block text-[10px] text-slate-500 marketing-font-mono uppercase tracking-widest mb-2">
                      What would you like to see? (optional)
                    </label>
                    <div className="relative">
                      <MessageSquare size={13} className="absolute left-3 top-3.5 text-slate-600" />
                      <textarea
                        name="message" value={form.message} onChange={handleChange}
                        rows={3}
                        placeholder="e.g. We handle 40 rotations per day across 3 stations. We're particularly interested in AI delay prediction and ramp execution..."
                        className="w-full bg-[#07111f] border border-white/10 focus:border-orange-500/50 text-white text-sm pl-9 pr-4 py-3 outline-none transition-colors resize-none marketing-font-body placeholder:text-slate-700"
                        data-testid="demo-message-textarea"
                      />
                    </div>
                  </div>

                  {error && (
                    <div className="text-red-400 text-xs mb-4 bg-red-500/5 border border-red-500/20 px-4 py-3">
                      {error}
                    </div>
                  )}

                  <button
                    type="submit" disabled={loading}
                    className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-400 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold py-4 text-sm transition-all hover:-translate-y-0.5"
                    data-testid="demo-request-submit-button"
                  >
                    {loading ? (
                      <span className="marketing-font-mono text-xs animate-pulse">SUBMITTING...</span>
                    ) : (
                      <><ArrowRight size={16} /> Request Demo Access</>
                    )}
                  </button>

                  <p className="text-[10px] text-slate-700 text-center mt-4">
                    No commitment required. We'll follow up within 4 hours on business days.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
